// @ts-nocheck
import translate from "@insite/client-framework/Translate";
import DynamicDropdown from "@insite/mobius/DynamicDropdown";
import { Checkbox, GridContainer, GridItem, Typography } from "@insite/mobius/index";
import getColor from "@insite/mobius/utilities/getColor";
import useLocalizeCurrency from "@store/Framework/Common/Hooks/useLocalizeCurrency";
import useReportApi from "@store/Framework/Common/Hooks/useReportAPI/useReportAPI";
import { expenditureReportExport } from "@store/Framework/Common/Utilities/excelExportFunctions";
import { ExpendituresItemModel } from "@store/Framework/Services/Reporting/ReportingModels";
import { isExpendituresModel } from "@store/Framework/Services/Reporting/ReportingTypeGuards";
import { reportParametersSelector } from "@store/Framework/Store/Bunzl/Pages/Reporting/ReportOrderFrequency/ReportOrderFrequencySelectors";
import useClear from "@store/Framework/Store/Bunzl/Pages/Reporting/StoreHooks/useClear";
import { CustomWidgetModule } from "@store/Framework/Types/types";
import ReportDataTable, { FormatDictionary } from "@store/Library/Components/Reporting/ApiDataTable/ReportDataTable";
import AccordionLayout from "@store/Library/Components/Reporting/Common/AccordionLayout";
import ApiButtons from "@store/Library/Components/Reporting/Common/ApiButtons";
import ButtonExportFile from "@store/Library/Components/Reporting/Common/ButtonExportFile";
import ReportContainer from "@store/Library/Components/Reporting/Common/ReportContainer";
import ReportingFilterGridItem from "@store/Library/Components/Reporting/Common/ReportingFilterGridItem";
import { LocationPicker } from "@store/Library/Components/Reporting/LocationPicker/LocationPicker";
import { ExportButtonGridItem } from "@store/Library/Widgets/Reports/InvoiceDetailSearchForm";
import { GridItemProps, GridWidths } from "@store/MobiusOverrides/GridItem/GridItem";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import React from "react";
import { useSelector } from "react-redux";
import styled, { css } from "styled-components";

export type ExpenditureLocationData = Omit<ExpendituresItemModel, "name" | "compname">;
export interface ExpenditureLocation {
    [locationNumber: string]: ExpendituresItemModel[];
}

export interface ExpenditureCompany {
    name: string;
    locations: ExpenditureLocation;
}
export interface ExpenditureCompanies {
    [companyNumber: string]: ExpenditureCompany;
}
export const getOrderTotalFromExpenditureLocationData = (data: ExpenditureLocationData[]) => {
    return data.reduce((total, item) => total + item.ordertotal, 0);
};
const getponums = (data: ExpendituresItemModel[]) => {
    return [...new Set(data.map(item => item.ponum))];
};
const gridItemStyle = {
    css: css`
        max-width: 342px !important;
    `,
    width: [12, 12, 3, 3, 3, 3] as unknown as GridWidths,
};
const LocationHeader = styled(Typography)`
    font-weight: 500;
    opacity: 90%;
`;
export const CompanyHeader = styled(Typography)`
    font-weight: 600;
    border-radius: 0.625rem;
    padding: 1rem;
    width: 100%;
    margin-bottom: 0.5rem;
    background-color: ${getColor("primary.main")};
    color: ${getColor("common.background")};
`;
const partialHeaders = ["Invoice #", "PO NUMBER", "Order Total"];
const partialDataKeys: (keyof ExpendituresItemModel)[] = ["invoicenum", "ponum", "ordertotal"];
const fullCostHeaders = ["Invoice #", "PO NUMBER", "Items Cost", "Tax", "Shipping", "Order Total"];
const fullCostDataKeys: (keyof ExpenditureLocationData)[] = [
    "invoicenum",
    "ponum",
    "itemstotal",
    "tax",
    "freightcharges",
    "ordertotal",
];
const DatePicker = getWidgetComponent("Reports/ReportSearchFieldDateRange");

const ExpendituresReport: React.FC = () => {
    const { isError, isLoading, updateData } = useReportApi("expenditure");
    const [ponums, setPonums] = React.useState<string[]>([]);
    const [showFullCost, setShowFullCost] = React.useState(false);
    const [selectedPO, setSelectedPO] = React.useState<string>("");
    const [expenditureCompanies, setExpenditureCompanies] = React.useState<ExpenditureCompanies>({});
    const localizeCurrency = useLocalizeCurrency();

    const formatDictionary: FormatDictionary = {
        itemstotal: localizeCurrency,
        tax: localizeCurrency,
        freightcharges: localizeCurrency,
        ordertotal: localizeCurrency,
    };

    const onClear = useClear();
    const reportParameters = useSelector(reportParametersSelector);

    const handleDataUpdate = async () => {
        const apiData = await updateData({ ...reportParameters, reportName: "expenditure", ponum: "" });
        if (isExpendituresModel(apiData)) {
            const companies: ExpenditureCompanies = {};
            setPonums(getponums(apiData.items));
            apiData.items.forEach(item => {
                const { companynumber, shiptonumber } = item;
                if (!companies[companynumber]) {
                    companies[companynumber] = { name: item.compname, locations: {} };
                }
                if (!companies[companynumber].locations[shiptonumber]) {
                    companies[companynumber].locations[shiptonumber] = [];
                }
                companies[companynumber].locations[shiptonumber].push(item);
            });
            setExpenditureCompanies(companies);
        }
    };
    let filteredCompanies: ExpenditureCompanies = {};
    if (selectedPO) {
        Object.entries(expenditureCompanies).forEach(([companyNumber, company]) => {
            filteredCompanies[companyNumber] = { name: company.name, locations: {} };

            Object.keys(expenditureCompanies[companyNumber].locations).forEach(location => {
                filteredCompanies[companyNumber].locations[location] = expenditureCompanies[companyNumber].locations[
                    location
                ].filter(item => item.ponum === selectedPO);
                if (filteredCompanies[companyNumber].locations[location].length === 0) {
                    delete filteredCompanies[companyNumber].locations[location];
                }
            });
            if (Object.keys(filteredCompanies[companyNumber].locations).length === 0) {
                delete filteredCompanies[companyNumber];
            }
        });
    } else {
        filteredCompanies = expenditureCompanies;
    }
    const options = ponums?.map(ponum => ({ optionValue: ponum, optionText: ponum }));
    options.concat({ optionValue: "", optionText: "All" });
    let headers: string[] = [];
    let dataKeys: (keyof ExpendituresItemModel)[] = [];
    if (showFullCost) {
        headers = fullCostHeaders;
        dataKeys = fullCostDataKeys;
    } else {
        headers = partialHeaders;
        dataKeys = partialDataKeys;
    }

    return (
        <ReportContainer>
            <AccordionLayout>
                <ReportingFilterGridItem doubleSized>
                    <DatePicker />
                </ReportingFilterGridItem>
                <ReportingFilterGridItem>
                    <LocationPicker isError={isError} />
                </ReportingFilterGridItem>
                <GridItem style={{ paddingBottom: "1.5rem" }} {...gridItemStyle}>
                    <Checkbox checked={showFullCost} onChange={() => setShowFullCost(cost => !cost)}>
                        {translate("Show Cost Breakdown")}
                    </Checkbox>
                </GridItem>
                <ApiButtons onClear={onClear} isLoading={isLoading} updateData={handleDataUpdate} />
                <ExportButtonGridItem width={12}>
                    <ButtonExportFile
                        excelData={expenditureReportExport(expenditureCompanies, fullCostDataKeys, fullCostHeaders)}
                        disabled={Object.keys(expenditureCompanies).length === 0}
                    />
                </ExportButtonGridItem>
                {options.length > 0 && (
                    <GridItem {...gridItemStyle}>
                        <DynamicDropdown
                            label={translate("PO Number")}
                            onSelectionChange={text => setSelectedPO(text || "")}
                            options={options}
                        />
                    </GridItem>
                )}
                {Object.values(filteredCompanies).map(data => (
                    <GridItem width={12} key={data.name} style={{ display: "flex", flexDirection: "column" }}>
                        <CompanyHeader variant="h3">{data.name}</CompanyHeader>
                        {Object.entries(data.locations).map(([location, data]) => (
                            <React.Fragment key={location}>
                                <LocationHeader variant="h4">
                                    {translate("LOCATION:")} {data[0].name}
                                </LocationHeader>
                                <Typography
                                    css={css`
                                        margin-bottom: 2rem;
                                    `}
                                    variant="h5"
                                >{`${translate("Total:")} ${localizeCurrency(
                                    getOrderTotalFromExpenditureLocationData(data),
                                )}`}</Typography>
                                <ReportDataTable
                                    dataTableData={{
                                        headers,
                                        dataKeys,
                                        data,
                                        title: location,
                                        formatDictionary,
                                    }}
                                />
                            </React.Fragment>
                        ))}
                    </GridItem>
                ))}
            </AccordionLayout>
        </ReportContainer>
    );
};

const widgetModule: CustomWidgetModule = {
    component: ExpendituresReport,
    definition: {
        group: "Reporting",
        displayName: "Expenditures Report",
    },
};

export default widgetModule;
